import React, { useEffect, memo } from 'react';
import { Text } from '@cuemath/web-comps';
import { Spinner } from '@cuemath/comps';

import { useFunnelAllPagesDetail, useFunnelMonthPages } from '../api/get-funnel';
import KpiCards from '../components/kpi-cards';
import PeriodChips from '../components/period-chips';
import TopSeriesGraphCard from '../components/top-series-graph';
import FunnelTable, { buildPageLandingColumns } from '../components/funnel-table';
import { SectionLabel, StateBlock, LoadingOverlay, UI } from '../styled-comps';
import {
  METRICS,
  METRIC_LABELS,
  EMPTY_MESSAGE,
  TOP_PAGES_COUNT,
} from '../constants';
import {
  asRows,
  metricFields,
  sumRows,
  channelLabel,
  normalizeHomepage,
  buildRangeParams,
  buildMonthlyTopSeries,
} from '../helpers';
import { useSettledKey } from '../hooks/use-settled-key';
import { usePeriodSelection } from '../hooks/use-period-selection';
import { useGraphControls } from '../hooks/use-graph-controls';

// Category tab (Brand / Intent / Content), product-spec v2:
//  * Year + month + region + user-type chips -> KPI cards + a table of EVERY
//    contributing signup page (rows sum exactly to the card totals).
//  * Independent graph section with its own chips: top N pages of the selected
//    year (ranked by QL over the whole year, outlier-proof) plotted month-on-month
//    for the selected metric.
const Category = memo(({ channel }) => {
  // ── table/cards section state: month-or-range period + region/user-type ──
  const { periodProps, window: tableWindow, label: periodLabel, regions, countries, userTypes } =
    usePeriodSelection();

  // ── graph section state (fully independent, its own Apply) ──
  const {
    graphProps,
    metric: graphMetric,
    appliedYear: graphYear,
    appliedRegions: graphRegions,
    appliedCountries: graphCountries,
    appliedUserTypes: graphUserTypes,
  } = useGraphControls();

  const {
    data: pagesData,
    get: getAllPages,
    isProcessing: pagesLoading,
    error: pagesError,
  } = useFunnelAllPagesDetail();
  const {
    data: monthPagesData,
    get: getMonthPages,
    isProcessing: graphLoading,
    error: graphError,
  } = useFunnelMonthPages();

  const tableQuery = buildRangeParams(tableWindow.start, tableWindow.end, {
    channel,
    regions,
    countries,
    userTypes,
  });
  const tableKey = JSON.stringify(tableQuery);
  useEffect(() => {
    getAllPages(`wm-allpagesdetail-${channel}`, tableQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey, channel]);

  const graphQuery = buildRangeParams(
    new Date(graphYear, 0, 1),
    new Date(graphYear, 11, 31),
    { channel, regions: graphRegions, countries: graphCountries, userTypes: graphUserTypes },
  );
  const graphKey = JSON.stringify(graphQuery);
  useEffect(() => {
    getMonthPages(`wm-monthpages-${channel}`, graphQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphKey, channel]);

  // True only once `pagesData` reflects the applied period — so the table/cards
  // never render the previous period's rows under the freshly-changed heading.
  const fresh = useSettledKey(tableKey, pagesLoading, pagesError);

  // ── table/cards derivation: (signup page x landing page) pairs ──
  const pageRows = (fresh ? asRows(pagesData) : [])
    .map(row => ({ label: row.page, landing: row.landing_page, ...metricFields(row) }))
    .sort((a, b) => b[METRICS.QL] - a[METRICS.QL]);
  const totals = sumRows(pageRows);

  // ── graph derivation: top pages by QL across the WHOLE selected year.
  // Homepage variants (/, /en-in/, /en-us/, ...) collapse into one 'Homepage'
  // entity so locale homepages don't crowd out real pages. ──
  const monthPageRows = asRows(monthPagesData);
  const graphDatasets = buildMonthlyTopSeries(
    monthPageRows,
    graphYear,
    graphMetric,
    row => normalizeHomepage(row.page),
  );

  const isFetching = pagesLoading || graphLoading;

  return (
    <>
      {isFetching && (
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      )}
      <SectionLabel>{channelLabel(channel)} — pick a period</SectionLabel>
      <PeriodChips {...periodProps} />

      <SectionLabel>{`${channelLabel(channel)} — ${periodLabel}`}</SectionLabel>
      {pagesError ? (
        <StateBlock>
          <Text variant="body1" color={UI.down}>{pagesError}</Text>
        </StateBlock>
      ) : (pagesLoading || !fresh) && !pageRows.length ? (
        <StateBlock>
          <Text variant="body1" color={UI.muted}>Loading {channelLabel(channel)} metrics…</Text>
        </StateBlock>
      ) : (
        <>
          <KpiCards totals={totals} compareTotals={null} />

          <SectionLabel>
            {`Contributing pages (${pageRows.length} signup × landing pairs) — rows sum to the totals above; the landing page is what put the lead in this category`}
          </SectionLabel>
          <FunnelTable
            data={pageRows}
            columns={buildPageLandingColumns()}
            loading={pagesLoading}
            defaultSorted={[{ id: METRICS.QL, desc: true }]}
            emptyMessage={EMPTY_MESSAGE}
            showTotals
          />
        </>
      )}

      <TopSeriesGraphCard
        {...graphProps}
        sectionLabel={`Top ${TOP_PAGES_COUNT} pages — month on month (ranked by QL over ${graphYear})`}
        caption={`Showing ${METRIC_LABELS[graphMetric]} per month for the top ${TOP_PAGES_COUNT} pages of ${graphYear}. Chips here don't affect the table above.`}
        datasets={graphDatasets}
        hasRawData={!!monthPageRows.length}
        loading={graphLoading}
        error={graphError}
      />
    </>
  );
});

export default Category;
